import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { isEmpty } from 'lodash-es';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Typographies } from 'models';
import { useKeyboardSubmission } from 'hooks';
import { resetPINIssuedCard } from 'redux/actions/issuing/reset';
import { getResetPINData, getResetPINLoading } from 'redux/selectors/issuing';
import { RESET_PIN_FORM_FIELDS } from 'consts';
import PassCode, { IPassCodeProps } from 'components/formElements/PassCode/PassCode';
import Typography from 'components/core/Typography/Typography';
import Button from 'components/core/Button/Button';
import { isNotValidFirstPin, isNotValidPin } from 'utilities/form/validations';
import Icon, { IconName } from 'components/core/Icon/Icon';
import { StyledText } from 'components/widgets/customerSupport/CustomerSupport';
import BaseReactFinalForm from 'components/formElements/BaseReactFinalForm';
import { boxRoundness, CtaButtonTextStyle } from 'styles/global';

interface IProps {
  token: string;
  codeLength: number;
  unAuthorizeCallback?: () => void;
  dispatchToastSuccessMsg?: boolean;
  useKeyboard?: boolean;
  isModal?: boolean;
}

const ResetPINWidget = ({
  token,
  codeLength,
  unAuthorizeCallback,
  dispatchToastSuccessMsg,
  useKeyboard = false,
  isModal = false
}: IProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const firstInputRef = useRef<any>(null);
  const secondInputRef = useRef<any>(null);
  const resetPINData = useSelector(getResetPINData);
  const loading = useSelector(getResetPINLoading);

  const [showPIN, toggleShowPIN] = useState(false);

  const resetSuccess = !isEmpty(resetPINData);
  const otpDisabled = loading || resetSuccess;

  const handleFirstInputCodeChange = (code: string, index: number) => {
    if (code.length === codeLength && index === codeLength - 1) {
      setTimeout(() => secondInputRef.current?.focus(), 0);
    }
  };

  const handleSecondInputCodeChange = (code: string) => {
    if (!code.length) {
      setTimeout(() => firstInputRef.current?.focus(), 0);
    }
  };

  const onReset = (values: any) => {
    const firstPin = values?.[RESET_PIN_FORM_FIELDS.firstPin];
    const secondPin = values?.[RESET_PIN_FORM_FIELDS.secondPin];
    if (isNotValidPin(codeLength)(firstPin, secondPin)) {
      return;
    }

    dispatch(
      resetPINIssuedCard({
        token,
        pin: firstPin,
        unAuthorizeCallback,
        dispatchToastSuccessMsg
      })
    );
  };

  useKeyboardSubmission({ handleSubmit: useKeyboard ? onReset : null });

  const renderShowHideEyeButton = () => (
    <ShowHideWrapper
      data-testid="show"
      disabled={loading}
      onClick={() => toggleShowPIN(!showPIN)}
    >
      {showPIN ? (
        <>
          <ShowHidePinLabel name="hide-pin" type={Typographies.BODY1}>
            {t('activate_issued_card.hide_pin')}
          </ShowHidePinLabel>
          <StyledIcon name={IconName.HIDE} />
        </>
      ) : (
        <>
          <ShowHidePinLabel name="show-pin" type={Typographies.BODY1}>
            {t('activate_issued_card.show_pin')}
          </ShowHidePinLabel>
          <StyledIcon name={IconName.SHOW} />
        </>
      )}
    </ShowHideWrapper>
  );

  const otpFixedWidthInRem = isModal ? 5 : '';

  return (
    <>
      {!isModal && (
        <StyledTitle name="reset-pin-title" type={Typographies.H7_HEADING}>
          {t('activate_issued_card.reset_pin')}
        </StyledTitle>
      )}
      <ModalFormWrapper isModal={isModal}>
        <BaseReactFinalForm
          onSubmit={onReset}
          render={({ values, valid, pristine, submitting, handleSubmit }) => (
            <StyledForm noValidate onSubmit={handleSubmit}>
              <ResetPINContent isModal={isModal}>
                <StyledText name="new-pin" type={Typographies.BODY1}>
                  {t('activate_issued_card.enter_pin')}
                </StyledText>
                <PinWrapper data-testid="new_pin">
                  <Field
                    name={RESET_PIN_FORM_FIELDS.firstPin}
                    render={props => (
                      <PassCode
                        {...(props as IPassCodeProps)}
                        ref={firstInputRef}
                        enforceSetModifiedSinceLastBlur={(
                          value: string,
                          index: number
                        ) => {
                          if (codeLength - 1 === index) return false;
                          return undefined;
                        }}
                      />
                    )}
                    otpDisabled={
                      otpDisabled || values[RESET_PIN_FORM_FIELDS.secondPin]?.length
                    }
                    codeLength={codeLength}
                    isMasked={!showPIN}
                    onChange={handleFirstInputCodeChange}
                    validate={value => isNotValidFirstPin(codeLength)(value)}
                    fixedWidth={otpFixedWidthInRem}
                  />
                </PinWrapper>
                <ConfirmPinWrapper>
                  <StyledText name="confirm-enter-pin" type={Typographies.BODY1}>
                    {t('activate_issued_card.enter_pin_confirmation')}
                  </StyledText>
                </ConfirmPinWrapper>
                <PinWrapper data-testid="confirm_pin">
                  <Field
                    name={RESET_PIN_FORM_FIELDS.secondPin}
                    component={PassCode}
                    enforceSetModifiedSinceLastBlur={(value: string, index: number) => {
                      const { [RESET_PIN_FORM_FIELDS.firstPin]: firstPin } = values || {};
                      if (codeLength - 1 === index) return false;
                      if (
                        firstPin?.length &&
                        firstPin.length === codeLength &&
                        value?.length &&
                        firstPin.slice(0, value.length) !== value
                      )
                        return false;
                      return undefined;
                    }}
                    ref={secondInputRef}
                    otpDisabled={
                      otpDisabled ||
                      values[RESET_PIN_FORM_FIELDS.firstPin]?.length !== codeLength
                    }
                    codeLength={codeLength}
                    isMasked={!showPIN}
                    onChange={handleSecondInputCodeChange}
                    validate={value =>
                      isNotValidPin(codeLength)(
                        values[RESET_PIN_FORM_FIELDS.firstPin],
                        value
                      )
                    }
                    fixedWidth={otpFixedWidthInRem}
                    showError
                  />
                </PinWrapper>
              </ResetPINContent>
              {renderShowHideEyeButton()}
              <StyledButton
                name="submit-reset-pin"
                isLoading={loading}
                disabled={!valid || pristine || submitting}
                isModal={isModal}
              >
                <StyledButtonText>{t('activate_issued_card.reset_pin')}</StyledButtonText>
              </StyledButton>
            </StyledForm>
          )}
        />
      </ModalFormWrapper>
    </>
  );
};

export default ResetPINWidget;

const ModalFormWrapper = styled.div<{ isModal: boolean }>`
  ${({ isModal }) =>
    isModal &&
    css`
      width: 100%;
      display: flex;
      flex-direction: column;
      margin-left: auto;
      margin-right: auto;
    `}
`;

const ResetPINContent = styled.div<{ isModal: boolean }>`
  margin-top: 1rem;
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: center;
  ${({ theme, isModal }) => {
    const { isMobile } = theme.view;

    if (isMobile) {
      return css`
        width: 100%;
      `;
    }

    return css`
      width: ${isModal ? '50%' : '70%'};
    `;
  }};
`;

const PinWrapper = styled.div`
  display: flex;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
`;

const StyledIcon = styled(Icon)`
  font-size: ${({ theme }) => theme.fonts.size.large};
  color: ${({ theme }) => theme.colors.link.primary};
`;

const ConfirmPinWrapper = styled.div`
  margin-top: 2.5rem;
`;

const ShowHideWrapper = styled.div<{ disabled: boolean }>`
  display: flex;
  align-self: center;

  > *:not(:last-child) {
    margin-right: 0.5rem;
  }
`;

const StyledTitle = styled(Typography)`
  margin-top: 2.5rem;
  color: ${({ theme }) => theme.colors.secondaryText};
`;

const ShowHidePinLabel = styled(Typography)`
  text-align: center;
  color: ${({ theme }) => theme.colors.link.primary};
`;

const StyledButton = styled(Button)<{ isModal: boolean }>`
  padding: 2rem ${({ isModal }) => (isModal ? '7.5rem' : '4rem')};
  ${boxRoundness};
  align-self: center;
  margin-top: 2.5rem;
  width: 100%;
`;

const StyledButtonText = styled.span`
  ${CtaButtonTextStyle};
  height: 2rem;
  line-height: 2rem;
  text-align: center;
  text-transform: capitalize;
`;
